import type { FormDataType } from '@inertiajs/core';
import { useHttp as inertiaUseHttp } from '@inertiajs/vue3';
import { HttpValidationError } from '@/Support/HttpErrors';

// `UseHttp` is not re-exported from the package root, only from its submodule.
type UseHttp<TForm extends FormDataType<TForm>, TResponse> = ReturnType<typeof inertiaUseHttp<TForm, TResponse>>;

const VERBS = ['get', 'post', 'put', 'patch', 'delete'] as const;

/**
 * Inertia's `useHttp` resolves a 422 with `undefined` rather than rejecting, so
 * a caller that distinguishes success from failure by try/catch (or by reading
 * the resolved body) silently treats a validation failure as an empty success.
 *
 * Rejecting instead means a call site cannot forget to handle it: a 422 lands
 * in the same catch as every other failure, and the resolved body is always
 * defined.
 */
export const useHttp = <TForm extends FormDataType<TForm>, TResponse = unknown>(
  data: TForm | (() => TForm),
): UseHttp<TForm, TResponse> => {
  const instance = inertiaUseHttp<TForm, TResponse>(data);

  VERBS.forEach((verb) => {
    const request = instance[verb];

    instance[verb] = (url, options) => request(url, options).then((response) => {
      if (response === undefined) {
        throw new HttpValidationError(url);
      }

      return response;
    });
  });

  return instance;
};
